import React, { useId } from 'react';
import { Field } from './Modal';

/**
 * Per-person working hours editor — shared by the Staff detail modal and the
 * Account page so both edit the SAME thing (stored in contractor_staff_info.
 * workingHours). When off, the person uses the company default hours
 * (Settings → Jobs and scheduling). Day toggles wrap; times sit in a responsive grid.
 *
 * props: { value: {days,dayStart,dayEnd}|null, onChange: (next|null) => void, disabled? }
 */
const WH_DAYS = [['mon', 'Mon', 'Monday'], ['tue', 'Tue', 'Tuesday'], ['wed', 'Wed', 'Wednesday'], ['thu', 'Thu', 'Thursday'], ['fri', 'Fri', 'Friday'], ['sat', 'Sat', 'Saturday'], ['sun', 'Sun', 'Sunday']];
export const DEFAULT_WORKING_HOURS = {
  days: { mon: true, tue: true, wed: true, thu: true, fri: true, sat: false, sun: false },
  dayStart: '08:00', dayEnd: '17:00',
};

/** Error text for a working-hours value, or null. */
export const workingHoursError = (wh) => {
  if (!wh) return null;
  if (!Object.values(wh.days || {}).some(Boolean)) return 'Pick at least one working day';
  if (wh.dayStart && wh.dayEnd && wh.dayEnd <= wh.dayStart) return 'The day must end after it starts';
  return null;
};

const WorkingHoursEditor = ({ value, onChange, disabled = false }) => {
  const uid = useId().replace(/:/g, '');
  const wh = value || null;
  const set = (patch) => onChange({ ...(wh || DEFAULT_WORKING_HOURS), ...patch });
  const error = workingHoursError(wh);
  return (
    <fieldset style={{ border: 0, padding: 0, margin: '0 0 16px' }} disabled={disabled}>
      <legend className="cw-label" style={{ marginBottom: 6 }}>Working hours</legend>
      <label htmlFor={`${uid}-own`} style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, cursor: 'pointer', color: 'var(--cw-text-2)' }}>
        <input id={`${uid}-own`} type="checkbox" checked={!!wh}
          onChange={(e) => onChange(e.target.checked ? { ...DEFAULT_WORKING_HOURS } : null)}
          style={{ accentColor: 'var(--cw-blue)', width: 18, height: 18 }} />
        <span>Set individual working hours <span className="cw-meta">(otherwise the company default applies)</span></span>
      </label>
      {wh && (
        <div style={{ marginTop: 6 }}>
          <div role="group" aria-label="Working days" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            {WH_DAYS.map(([k, lbl, full]) => {
              const on = !!(wh.days && wh.days[k]);
              return (
                <button key={k} type="button" aria-pressed={on} aria-label={full}
                  onClick={() => set({ days: { ...(wh.days || {}), [k]: !on } })}
                  style={{
                    minWidth: 44, minHeight: 44, padding: '0 0.7rem', borderRadius: 8, border: '1px solid',
                    borderColor: on ? 'var(--cw-blue)' : 'var(--cw-border-strong)',
                    background: on ? 'var(--cw-blue-tint)' : 'var(--cw-surface)',
                    color: on ? 'var(--cs-blue-dark, #1565C0)' : 'var(--cw-text-muted)',
                    fontSize: 'var(--cs-text-sm)', fontWeight: 600, cursor: 'pointer',
                  }}>
                  {on && <i className="fa-solid fa-check" aria-hidden="true" style={{ marginRight: 4 }} />}{lbl}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(130px, 100%), 1fr))', gap: 12 }}>
            <Field label="Day starts"><input type="time" value={wh.dayStart || '08:00'} onChange={(e) => set({ dayStart: e.target.value })} /></Field>
            <Field label="Day ends"><input type="time" value={wh.dayEnd || '17:00'} onChange={(e) => set({ dayEnd: e.target.value })} /></Field>
          </div>
          {error && <p className="cw-field-error" role="alert">{error}</p>}
        </div>
      )}
    </fieldset>
  );
};

export default WorkingHoursEditor;
